import React, { useState, useEffect } from 'react';

interface StandbyScreenProps {
  onWake: () => void;
  isLocked?: boolean;
  onUnlock?: (pin: string) => boolean;
  // Customization options
  logoImage?: string; // Path to logo image (e.g., '/logo.png' or 'https://example.com/logo.png')
  logoText?: string; // Text to display if no logo image (default: 'KINGS PARK')
  backgroundImage?: string; // Path to background image
  backgroundColor?: string; // Background color if no image (default: gradient)
  brandSubtext?: string; // Subtitle text (default: 'Restaurant POS System')
}

const StandbyScreen: React.FC<StandbyScreenProps> = ({ 
  onWake, 
  isLocked = false, 
  onUnlock,
  logoImage,
  logoText = 'KINGS PARK',
  backgroundImage,
  backgroundColor,
  brandSubtext = 'Restaurant POS System'
}) => {
  const [time, setTime] = useState(new Date());
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleClick = () => {
    if (!isLocked) {
      onWake();
    }
  };

  const handlePinChange = (newPin: string) => {
    setError('');
    setPin(newPin);
    if (newPin.length === 4 && onUnlock) {
      const success = onUnlock(newPin);
      if (success) {
        setPin('');
        onWake();
      } else {
        setError('Invalid PIN. Please try again.');
        setTimeout(() => setPin(''), 500);
      }
    }
  };

  const formattedTime = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const formattedDate = time.toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  // Determine background style
  const backgroundStyle: React.CSSProperties = {};
  if (backgroundImage) {
    backgroundStyle.backgroundImage = `url(${backgroundImage})`;
    backgroundStyle.backgroundSize = 'cover';
    backgroundStyle.backgroundPosition = 'center';
    backgroundStyle.backgroundRepeat = 'no-repeat';
  } else if (backgroundColor) {
    backgroundStyle.backgroundColor = backgroundColor;
  }

  // Determine background classes
  const backgroundClasses = backgroundImage || backgroundColor 
    ? '' 
    : 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900';

  return (
    <div 
      className={`h-screen w-screen ${backgroundClasses} flex flex-col justify-center items-center font-sans text-white cursor-pointer relative`}
      style={backgroundStyle}
      onClick={handleClick}
    >
      {/* Overlay for better text readability when using background image */}
      {backgroundImage && (
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
      )}
      
      {/* Content container */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Brand Logo/Name */}
        <div className="text-center mb-12 animate-pulse">
          {logoImage ? (
            <div className="mb-4">
              <img 
                src={logoImage} 
                alt={logoText}
                className="max-w-md max-h-48 mx-auto object-contain"
                style={{ filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3))' }}
              />
            </div>
          ) : (
            <>
              <h1 className="text-8xl font-bold tracking-widest mb-4">{logoText}</h1>
              <div className="w-32 h-1 bg-pink-500 mx-auto"></div>
            </>
          )}
          {brandSubtext && (
            <p className="text-gray-400 mt-4 text-xl">{brandSubtext}</p>
          )}
        </div>

        {/* Time and Date */}
        <div className="text-center mb-8">
          <h2 className="text-7xl font-light tracking-wider mb-2">{formattedTime}</h2>
          <p className="text-gray-400 text-lg">{formattedDate}</p>
        </div>

        {/* Lock Status and PIN Entry */}
        {isLocked && (
          <div className="mt-8 w-full max-w-xs">
          <p className="text-center text-gray-300 mb-4 text-lg">Register is locked</p>
          <p className="text-center text-gray-400 mb-6 text-sm">Enter your PIN to unlock</p>
          
          <div className="flex justify-center items-center h-16 mb-4">
            <div className="flex space-x-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className={`w-4 h-4 rounded-full transition-colors duration-200 ${
                    pin.length > i ? 'bg-pink-500' : 'bg-gray-700'
                  }`}
                ></div>
              ))}
            </div>
          </div>
          
          {error && <p className="text-red-400 text-center text-sm mb-4 h-5">{error}</p>}
          {!error && <div className="h-5 mb-4"></div>}
          
          {/* Simple PIN pad */}
          <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <button
                key={num}
                onClick={(e) => {
                  e.stopPropagation();
                  if (pin.length < 4) {
                    handlePinChange(pin + num.toString());
                  }
                }}
                className="bg-gray-700 hover:bg-gray-600 text-white text-2xl font-semibold py-4 rounded-lg transition-colors"
              >
                {num}
              </button>
            ))}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPin('');
                setError('');
              }}
              className="bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold py-4 rounded-lg transition-colors col-span-2"
            >
              Clear
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (pin.length < 4) {
                  handlePinChange(pin + '0');
                }
              }}
              className="bg-gray-700 hover:bg-gray-600 text-white text-2xl font-semibold py-4 rounded-lg transition-colors"
            >
              0
            </button>
            </div>
          </div>
        )}
      </div>

      {/* Wake up hint */}
      {!isLocked && (
        <p className="text-gray-500 text-sm mt-8 animate-pulse relative z-10">Tap anywhere to wake up</p>
      )}
    </div>
  );
};

export default StandbyScreen;

